/**
 * client.ts — the only place in this repository that speaks to the server.
 *
 * Everything the workbench knows about the plant arrives through here, and the
 * three rules the API imposes are enforced once, at this seam, rather than
 * remembered at every call site:
 *
 *   • The session is an HttpOnly cookie the script cannot read (§7.2), so every
 *     request is `credentials: "same-origin"` and no token is ever held in JS.
 *   • Every unsafe request carries the CSRF token from the readable companion
 *     cookie (§16.1). A handler cannot forget it, because it is added here.
 *   • Every failure is the typed contract (§10.5), turned into an `ApiError`
 *     with a code the UI can branch on.
 */

import { ApiError, TransportError, toApiError } from "./errors";
import { fetchCapabilityManifest, type CapabilityManifest } from "./capabilityManifest";
import type {
  ApplicationEvent, AuditEvent, AuthoringPreview, AuthoringRequest, AuthoringResult,
  ChangeSet, ChangeSetSummary, DiagramSummary, DiagramView, JobView, ProjectStatus,
  ProjectSummary, SessionView, SymbolCatalogEntry, ValidationSummary,
} from "../types/contract";

/** The cookie the server marks readable ON PURPOSE, for double-submit CSRF. */
const CSRF_COOKIE = "plantlifecycle_csrf";
const CSRF_HEADER = "x-plantlifecycle-csrf";

export interface ClientOptions {
  /** Base path of the API. Same origin by default — §15.2 deploys it that way. */
  baseUrl?: string;
  /** Injectable for tests; defaults to the global. */
  fetchImpl?: typeof fetch;
  /** Injectable for tests; defaults to reading `document.cookie`. */
  readCookie?: (name: string) => string | undefined;
}

function cookieFromDocument(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return undefined;
}

export class PlantLifecycleClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly readCookie: (name: string) => string | undefined;

  constructor(options: ClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.readCookie = options.readCookie ?? cookieFromDocument;
  }

  // -- session --------------------------------------------------------------

  session(): Promise<SessionView> { return this.get("/api/v1/session"); }

  /**
   * The Engine's published capability manifest, negotiated for the contract
   * this build serves and validated before anything is rendered from it.
   *
   * This is the ONLY way the browser learns what the Engine supports. There is
   * deliberately no local catalog of operations or queries beside it, so a
   * compatible new Engine capability is usable here without a semantic edit.
   */
  capabilities(options: { expectedDigest?: string; signal?: AbortSignal } = {}): Promise<CapabilityManifest> {
    return fetchCapabilityManifest({
      fetchImpl: this.fetchImpl,
      baseUrl: this.baseUrl,
      ...(options.expectedDigest !== undefined ? { expectedDigest: options.expectedDigest } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  }

  /**
   * Start sign-in.
   *
   * A full navigation, not a fetch: the OIDC flow ends in a redirect back to
   * this origin carrying the session cookie, and a redirect cannot be followed
   * by XHR in a way that sets cookies the browser will keep.
   */
  loginUrl(redirectTo: string): string {
    return `${this.baseUrl}/api/v1/session/login?redirectTo=${encodeURIComponent(redirectTo)}`;
  }

  logout(): Promise<{ authenticated: boolean }> { return this.post("/api/v1/session/logout", undefined); }

  // -- projects -------------------------------------------------------------

  listProjects(): Promise<ProjectSummary[]> { return this.get("/api/v1/projects"); }

  projectStatus(projectId: string): Promise<ProjectStatus> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/status`);
  }

  // -- diagrams -------------------------------------------------------------

  listDiagrams(projectId: string): Promise<DiagramSummary[]> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/diagrams`);
  }

  readDiagram(projectId: string, diagramId: string): Promise<DiagramView> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/diagrams/${encodeURIComponent(diagramId)}`);
  }

  // -- validation and queries ----------------------------------------------

  validate(projectId: string, diagramId?: string): Promise<ValidationSummary> {
    const query = diagramId ? `?diagramId=${encodeURIComponent(diagramId)}` : "";
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/validation${query}`);
  }

  runQuery(projectId: string, intent: string, params: Record<string, unknown> = {}): Promise<unknown> {
    return this.post(`/api/v1/projects/${encodeURIComponent(projectId)}/queries`, { intent, params });
  }

  /**
   * The generic READ path, addressed by whatever the Engine's manifest
   * published. The caller passes a fully derived body from `capabilityAction`;
   * this method moves bytes and adds nothing.
   */
  capabilityRead(projectId: string, body: unknown): Promise<unknown> {
    return this.post(`/api/v1/projects/${encodeURIComponent(projectId)}/queries`, body);
  }

  /**
   * The generic REVIEWED-MUTATION path, preview then apply.
   *
   * `preview` writes nothing and is safe on every pointer release. `apply`
   * records working state; publication remains the only canonical write, and it
   * belongs to the change-set flow below, not to this method.
   */
  capabilityPreview(projectId: string, changeSetId: string, request: AuthoringRequest): Promise<unknown> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/authoring/preview`, request);
  }

  capabilityApply(projectId: string, changeSetId: string, request: AuthoringRequest): Promise<unknown> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/authoring/apply`, request);
  }

  // -- change sets ----------------------------------------------------------

  listChangeSets(projectId: string): Promise<ChangeSetSummary[]> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/change-sets`);
  }

  createChangeSet(projectId: string, title: string): Promise<ChangeSet> {
    return this.post(`/api/v1/projects/${encodeURIComponent(projectId)}/change-sets`, { title });
  }

  getChangeSet(projectId: string, changeSetId: string): Promise<ChangeSet> {
    return this.get(this.changeSetPath(projectId, changeSetId));
  }

  submitForReview(projectId: string, changeSetId: string): Promise<ChangeSet> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/submit`, {});
  }

  approve(projectId: string, changeSetId: string): Promise<ChangeSet> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/approve`, {});
  }

  requestChanges(projectId: string, changeSetId: string, note: string): Promise<ChangeSet> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/request-changes`, { note });
  }

  publish(projectId: string, changeSetId: string, message?: string): Promise<ChangeSet> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/publish`, message === undefined ? {} : { message });
  }

  abandon(projectId: string, changeSetId: string): Promise<ChangeSet> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/abandon`, {});
  }

  // -- authoring ------------------------------------------------------------

  /** §12 — what the Engine WOULD do. Nothing is written. */
  previewAuthoring(projectId: string, changeSetId: string, request: AuthoringRequest): Promise<AuthoringPreview> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/authoring/preview`, request);
  }

  applyAuthoring(projectId: string, changeSetId: string, request: AuthoringRequest): Promise<AuthoringResult> {
    return this.post(`${this.changeSetPath(projectId, changeSetId)}/authoring/apply`, request);
  }

  // -- symbols, jobs, audit -------------------------------------------------

  symbolCatalog(projectId: string): Promise<SymbolCatalogEntry[]> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/symbols`);
  }

  job(projectId: string, jobId: string): Promise<JobView> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}`);
  }

  audit(projectId: string, limit = 100): Promise<AuditEvent[]> {
    return this.get(`/api/v1/projects/${encodeURIComponent(projectId)}/audit?limit=${limit}`);
  }

  /**
   * Subscribe to the project's event stream (§10.4).
   *
   * Returns the unsubscribe function. The server already filters by project, so
   * a subscriber cannot observe a project it has no membership of; this side
   * simply reconnects, because `EventSource` gives up after some failures and a
   * workbench that silently stops receiving revisions is worse than one that
   * says it is offline.
   */
  subscribeEvents(projectId: string, onEvent: (event: ApplicationEvent) => void, onStateChange?: (open: boolean) => void): () => void {
    const url = `${this.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/events`;
    // A host without EventSource (a test renderer, a very old browser) gets a
    // workbench that polls on demand rather than one that fails to render.
    if (typeof EventSource === "undefined") { onStateChange?.(false); return () => undefined; }
    let source: EventSource | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    const connect = (): void => {
      if (closed) return;
      source = new EventSource(url, { withCredentials: true });
      source.onopen = () => onStateChange?.(true);
      source.onmessage = message => deliver(message.data);
      for (const kind of EVENT_KINDS) source.addEventListener(kind, event => deliver((event as MessageEvent).data));
      source.onerror = () => {
        onStateChange?.(false);
        source?.close();
        if (!closed) retry = setTimeout(connect, 3_000);
      };
    };

    const deliver = (data: unknown): void => {
      if (typeof data !== "string") return;
      try { onEvent(JSON.parse(data) as ApplicationEvent); }
      catch { /* a malformed frame is dropped; the next poll re-reads the truth */ }
    };

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      source?.close();
      onStateChange?.(false);
    };
  }

  // -- transport ------------------------------------------------------------

  private changeSetPath(projectId: string, changeSetId: string): string {
    return `/api/v1/projects/${encodeURIComponent(projectId)}/change-sets/${encodeURIComponent(changeSetId)}`;
  }

  private get<T>(path: string): Promise<T> { return this.request<T>("GET", path, undefined); }
  private post<T>(path: string, body: unknown): Promise<T> { return this.request<T>("POST", path, body); }

  private async request<T>(method: "GET" | "POST", path: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    if (method !== "GET") {
      headers["content-type"] = "application/json";
      const csrf = this.readCookie(CSRF_COOKIE);
      // Send it when present. A missing token is not silently tolerated — the
      // server refuses with FORBIDDEN, which is the correct outcome for a
      // session that is not actually established.
      if (csrf) headers[CSRF_HEADER] = csrf;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        credentials: "same-origin",
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      throw new TransportError(`The request to ${path} did not complete.`, cause);
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    let payload: unknown;
    try { payload = text ? JSON.parse(text) : undefined; }
    catch {
      if (!response.ok) throw toApiError(response.status, undefined);
      throw new TransportError(`The server answered ${path} with a body that is not JSON.`);
    }

    if (!response.ok) throw toApiError(response.status, payload);
    return payload as T;
  }
}

const EVENT_KINDS = [
  "project.revision.changed", "changeset.status.changed", "validation.completed",
  "job.status.changed", "presence.changed", "conflict.detected",
] as const;

export { ApiError, TransportError };
